(() => {
  "use strict";

  document.documentElement.classList.add("mr-moments-js");

  const initialized = {
    roots: new WeakSet(),
    forms: new WeakSet(),
    drawers: new WeakSet(),
    reveals: new WeakSet()
  };
  const state = { config: null };

  const safeText = (value, fallback = "") => {
    if (typeof value === "string") return value.trim() || fallback;
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
    return fallback;
  };

  const safeUrl = (value, allowRelative = false) => {
    const raw = safeText(value);
    if (!raw) return "";
    if (allowRelative && raw.startsWith("/") && !raw.startsWith("//")) return raw;
    try {
      const parsed = new URL(raw, window.location.origin);
      return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : "";
    } catch (_) {
      return "";
    }
  };

  const endpointWithScope = (endpoint) => {
    const divider = endpoint.includes("?") ? "&" : "?";
    return `${endpoint}${divider}scope=moments_page`;
  };

  const parseResponse = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch (_) {
      throw new Error(response.ok ? "We couldn't complete your request. Please try again." : "MamaRaya Moments is temporarily unavailable.");
    }
  };

  const setBoundText = (key, value) => {
    const next = safeText(value);
    if (!next) return;
    document.querySelectorAll(`[data-mr-bind="${key}"]`).forEach((node) => {
      node.textContent = next;
    });
  };

  const setBoundLink = (key, value) => {
    const next = safeUrl(value, true);
    if (!next) return;
    document.querySelectorAll(`[data-mr-link="${key}"]`).forEach((node) => {
      node.setAttribute("href", next);
      node.hidden = false;
    });
  };

  const applyCampaignConfig = (config) => {
    const campaign = config?.campaign;
    if (!campaign || typeof campaign !== "object") return;

    setBoundText("campaignLabel", campaign.label);
    setBoundText("entryMonth", campaign.entryMonthLabel || campaign.entryMonth);
    setBoundText("heroHeading", campaign.heroHeading);
    setBoundText("heroIntro", campaign.heroIntro);
    setBoundText("rewardValue", campaign.reward?.valueLabel);
    setBoundText("rewardTitle", campaign.reward?.title);
    setBoundText("rewardDetails", campaign.reward?.details);
    setBoundText("winnerTiming", campaign.winnerTimingLabel);
    setBoundText("socialHandle", campaign.socialHandle);
    setBoundText("hashtag", campaign.hashtag);
    const rewardImage = safeUrl(campaign.reward?.imageUrl);
    let imageNodes = [...document.querySelectorAll("[data-mr-prize-image]")];
    if (rewardImage) {
      if (!imageNodes.length) {
        document.querySelectorAll(".mr-moments-prize__image-wrap").forEach((wrap) => {
          const image = document.createElement("img");
          image.className = "mr-moments-prize__image";
          image.loading = "eager";
          image.fetchPriority = "high";
          image.width = 900;
          image.height = 900;
          image.dataset.mrPrizeImage = "";
          wrap.prepend(image);
          imageNodes.push(image);
        });
      }
      imageNodes.forEach((image) => {
        image.src = rewardImage;
        image.alt = safeText(campaign.reward?.imageAlt, "This month's MamaRaya Moments reward");
        image.hidden = false;
      });
      document.querySelectorAll("[data-mr-prize-placeholder]").forEach((node) => { node.hidden = true; });
    }

    document.querySelectorAll("[data-mr-entry-month]").forEach((input) => {
      input.value = safeText(campaign.entryMonth);
    });

    const form = document.querySelector("[data-mr-entry-form]");
    const submit = form?.querySelector("[type='submit']");
    const open = campaign.entryOpen !== false;
    if (submit) submit.disabled = !open;
    if (!open) {
      setFormStatus(form, safeText(campaign.entryClosedMessage, "Entries are closed for this month."), "error");
    }
  };

  const applyTrustpilotConfig = (config) => {
    const trust = config?.trustpilot;
    if (!trust || typeof trust !== "object") return;
    setBoundText("trustScore", trust.scoreLabel);
    setBoundText("trustReviewCount", trust.reviewCountLabel);
    setBoundText("trustHeading", trust.heading);
    setBoundText("trustCopy", trust.description);
    setBoundLink("trustReviewUrl", trust.reviewUrl);
    setBoundLink("trustProfileUrl", trust.profileUrl);
    const scoreBlock = document.querySelector("[data-mr-trust-score-block]");
    if (scoreBlock && safeText(trust.scoreLabel)) scoreBlock.hidden = false;
  };

  const createWinnerCard = (entry) => {
    const card = document.createElement("article");
    card.className = "mr-moments-winner";

    const imageUrl = safeUrl(entry?.imageUrl);
    if (imageUrl) {
      const image = document.createElement("img");
      image.src = imageUrl;
      image.alt = safeText(entry?.imageAlt, "MamaRaya Moments winner");
      image.loading = "lazy";
      image.width = 720;
      image.height = 900;
      card.append(image);
    }

    const copy = document.createElement("div");
    copy.className = "mr-moments-winner__copy";
    const month = document.createElement("span");
    month.textContent = safeText(entry?.monthLabel, "MamaRaya Moments");
    const title = document.createElement("h3");
    title.textContent = safeText(entry?.publicNickname, "MamaRaya customer");
    copy.append(month, title);

    const postUrl = safeUrl(entry?.socialPostUrl);
    if (postUrl) {
      const link = document.createElement("a");
      link.href = postUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = "View the winning entry";
      copy.append(link);
    }

    card.append(copy);
    return card;
  };

  const createWinnerMonth = (month, index) => {
    const slide = document.createElement("section");
    slide.className = "mr-moments-winner-month";
    slide.dataset.mrWinnerMonth = "";
    slide.setAttribute("aria-label", safeText(month?.monthLabel, `Winner month ${index + 1}`));

    const head = document.createElement("header");
    const label = document.createElement("p");
    label.textContent = safeText(month?.monthLabel, "MamaRaya Moments");
    const count = document.createElement("span");
    count.textContent = String(index + 1).padStart(2, "0");
    head.append(label, count);

    const entries = Array.isArray(month?.entries) ? month.entries.filter(Boolean).slice(0, 2) : [];
    const grid = document.createElement("div");
    grid.className = `mr-moments-winner-month__grid${entries.length === 1 ? " mr-moments-winner-month__grid--single" : ""}`;
    grid.append(...entries.map(createWinnerCard));
    slide.append(head, grid);
    return slide;
  };

  const applyReturnConfig = (config) => {
    const section = document.querySelector("[data-mr-return]");
    if (!section) return;

    const winners = config?.winnerBoard;
    const promotion = config?.promotion;
    const winnerPanel = section.querySelector("[data-mr-winners]");
    const winnerTrack = section.querySelector("[data-mr-winner-track]");
    const promoPanel = section.querySelector("[data-mr-promo]");

    let showWinners = false;
    let showPromotion = false;

    const winnerMonths = Array.isArray(winners?.months)
      ? winners.months.map((month) => ({ ...month, entries: Array.isArray(month?.winners) ? month.winners : month?.entries })).filter((month) => Array.isArray(month.entries) && month.entries.length)
      : Array.isArray(winners?.entries) && winners.entries.length
        ? [{ monthLabel: winners.monthLabel || winners.entries[0]?.monthLabel, entries: winners.entries }]
        : [];

    if (winners?.enabled === true && winnerMonths.length) {
      showWinners = true;
      if (winnerTrack) {
        winnerTrack.replaceChildren(...winnerMonths.map(createWinnerMonth));
      }
      setBoundText("winnerHeading", winners.heading);
      setBoundText("winnerIntro", winners.description);
    }

    if (promotion?.enabled === true) {
      const imageUrl = safeUrl(promotion.imageUrl);
      const linkUrl = safeUrl(promotion.linkUrl, true);
      if (imageUrl && promoPanel) {
        const anchor = document.createElement(linkUrl ? "a" : "div");
        if (linkUrl) anchor.setAttribute("href", linkUrl);
        const image = document.createElement("img");
        image.src = imageUrl;
        image.alt = safeText(promotion.imageAlt, "MamaRaya promotion");
        image.loading = "lazy";
        image.width = 720;
        image.height = 900;
        anchor.append(image);
        promoPanel.replaceChildren(anchor);
        const width = Math.min(100, Math.max(35, Number(promotion.widthPercent) || 100));
        promoPanel.style.setProperty("--mr-promo-width", `${width}%`);
        showPromotion = true;
      }
    }

    if (winnerPanel) winnerPanel.hidden = !showWinners;
    if (promoPanel) promoPanel.hidden = !showPromotion;
    section.classList.toggle("mr-moments-return--promo-only", !showWinners && showPromotion);
    section.classList.toggle("mr-moments-return--winners-only", showWinners && !showPromotion);
    section.hidden = !(showWinners || showPromotion);
    document.querySelectorAll("[data-mr-return-link]").forEach((link) => { link.hidden = section.hidden; });
  };

  const applyConfig = (payload) => {
    const config = payload?.momentsPage || payload?.page || payload?.moments;
    if (!config || typeof config !== "object") return;
    state.config = config;
    applyCampaignConfig(config);
    applyTrustpilotConfig(config);
    applyReturnConfig(config);
  };

  const loadConfig = async (root) => {
    const endpoint = safeText(root.dataset.mrEndpoint, "/apps/mamaraya-ugc");
    try {
      const response = await fetch(endpointWithScope(endpoint), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      });
      if (!response.ok) return;
      applyConfig(await parseResponse(response));
    } catch (_) {
      /* Static fallbacks remain usable when the optional page config is unavailable. */
    }
  };

  const setFormStatus = (form, message, status = "") => {
    const node = form?.querySelector("[data-mr-form-status]");
    if (!node) return;
    node.textContent = safeText(message);
    node.dataset.state = status;
  };

  const submitEntry = async (form) => {
    const button = form.querySelector("[type='submit']");
    if (!form.reportValidity()) return;

    const formData = new FormData(form);
    const contentUrl = safeUrl(formData.get("contentUrl"));
    if (!contentUrl) {
      setFormStatus(form, "Enter the full public link, starting with https://", "error");
      form.elements.contentUrl?.focus();
      return;
    }

    const payload = {
      operation: "submit_social_entry",
      entryMonth: safeText(formData.get("entryMonth") || state.config?.campaign?.entryMonth),
      entryMethod: "PURCHASE_ORDER",
      publicNickname: safeText(formData.get("publicNickname")),
      email: safeText(formData.get("email")).toLowerCase(),
      entryType: safeText(formData.get("entryType")),
      contentUrl,
      orderNumber: safeText(formData.get("orderNumber"))
    };

    button.disabled = true;
    button.dataset.originalLabel = button.textContent;
    button.textContent = "Verifying your order…";
    setFormStatus(form, "We're checking your order and link.");

    if (form.dataset.mrDemo === "true") {
      window.setTimeout(() => {
        setFormStatus(form, "Preview only: the live app will verify and save this entry.", "success");
        button.disabled = false;
        button.textContent = button.dataset.originalLabel;
      }, 500);
      return;
    }

    const endpoint = safeText(form.dataset.mrEndpoint, "/apps/mamaraya-ugc");
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload)
      });
      const result = await parseResponse(response);
      if (!response.ok || result.ok === false) {
        throw new Error(safeText(result.message, "We couldn't verify your entry. Check your information and try again."));
      }
      form.reset();
      setFormStatus(form, safeText(result.message, "You're entered. We sent a confirmation email."), "success");
    } catch (error) {
      setFormStatus(form, safeText(error?.message, "MamaRaya Moments is temporarily unavailable. Please try again shortly."), "error");
    } finally {
      button.disabled = false;
      button.textContent = button.dataset.originalLabel;
    }
  };

  const setupForm = (form) => {
    if (!form || initialized.forms.has(form)) return;
    initialized.forms.add(form);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      submitEntry(form);
    });
  };

  const setupDrawer = (root) => {
    const trigger = root.querySelector("[data-mr-drawer-open]");
    const drawer = root.querySelector("[data-mr-drawer]");
    const scrim = root.querySelector("[data-mr-drawer-scrim]");
    if (!trigger || !drawer || initialized.drawers.has(drawer)) return;
    initialized.drawers.add(drawer);

    let previousFocus = null;
    let previousOverflow = "";
    const close = () => {
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      trigger.setAttribute("aria-expanded", "false");
      document.documentElement.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
    const open = () => {
      previousFocus = document.activeElement;
      previousOverflow = document.documentElement.style.overflow;
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      trigger.setAttribute("aria-expanded", "true");
      document.documentElement.style.overflow = "hidden";
      drawer.querySelector("[data-mr-drawer-close]")?.focus();
    };

    trigger.addEventListener("click", open);
    drawer.querySelector("[data-mr-drawer-close]")?.addEventListener("click", close);
    scrim?.addEventListener("click", close);
    drawer.querySelectorAll("a[href^='#']").forEach((link) => link.addEventListener("click", close));
    document.addEventListener("keydown", (event) => {
      if (!drawer.classList.contains("is-open")) return;
      if (event.key === "Escape") {
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...drawer.querySelectorAll("a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])")];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const setupReveal = () => {
    const nodes = document.querySelectorAll(".mr-moments [data-mr-reveal], .mr-moments [data-mr-image-reveal]");
    if (!("IntersectionObserver" in window)) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -8%", threshold: 0.08 });
    nodes.forEach((node) => {
      if (initialized.reveals.has(node)) return;
      initialized.reveals.add(node);
      observer.observe(node);
    });
  };

  const setupProgress = (root) => {
    const links = [...root.querySelectorAll("[data-mr-progress-link]")];
    if (!links.length) return;

    let frame = 0;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const targets = () => links.map((link) => {
      const section = document.querySelector(link.getAttribute("href"));
      if (!section || link.hidden || section.hidden || getComputedStyle(section).display === "none") return null;
      return { link, section };
    }).filter(Boolean);

    const stickyHeaderHeight = () => [...document.querySelectorAll("header, .section-header, .shopify-section-header-sticky")]
      .reduce((height, element) => {
        const style = getComputedStyle(element);
        const box = element.getBoundingClientRect();
        if ((style.position !== "sticky" && style.position !== "fixed") || box.height <= 0) return height;
        return Math.max(height, box.height);
      }, 0);

    const setCurrent = (activeLink) => {
      links.forEach((link) => link.setAttribute("aria-current", String(link === activeLink)));
    };

    const update = () => {
      frame = 0;
      const available = targets();
      if (!available.length) return;

      const headerHeight = stickyHeaderHeight();
      const probe = window.scrollY + headerHeight + Math.max(88, (window.innerHeight - headerHeight) * 0.3);
      let active = available[0];

      available.forEach((item) => {
        const top = item.section.getBoundingClientRect().top + window.scrollY;
        if (top <= probe) active = item;
      });

      const pageBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
      if (pageBottom) active = available[available.length - 1];
      setCurrent(active.link);
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    links.forEach((link) => {
      link.addEventListener("click", (event) => {
        const section = document.querySelector(link.getAttribute("href"));
        if (!section || section.hidden) return;
        event.preventDefault();
        const top = section.getBoundingClientRect().top + window.scrollY - stickyHeaderHeight() - 18;
        setCurrent(link);
        window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion.matches ? "auto" : "smooth" });
        if (history.replaceState) history.replaceState(null, "", link.getAttribute("href"));
      });
    });

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate, { passive: true });
    window.addEventListener("load", scheduleUpdate, { once: true });

    const visibilityObserver = new MutationObserver(scheduleUpdate);
    links.forEach((link) => visibilityObserver.observe(link, { attributes: true, attributeFilter: ["hidden"] }));
    links.map((link) => document.querySelector(link.getAttribute("href"))).filter(Boolean)
      .forEach((section) => visibilityObserver.observe(section, { attributes: true, attributeFilter: ["hidden"] }));

    scheduleUpdate();
  };

  const setupRoot = (root) => {
    if (initialized.roots.has(root)) return;
    initialized.roots.add(root);
    setupDrawer(root);
    setupProgress(root);
    loadConfig(root);
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.("[data-mr-config-root]").forEach(setupRoot);
    scope.querySelectorAll?.("[data-mr-entry-form]").forEach(setupForm);
    setupReveal();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
